import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DRUM_CHANNEL, parseMidi, type Midi, type MidiChannel } from "@midi-fantom-utils/midi-core";
import {
  buildPattern,
  buildPlaybackData,
  buildReport,
  buildTrackLanes,
  countNotes,
  dropMutedLaneNotes,
  formatPatternId,
  measureBoundaries,
  toFantomName,
  toMultiTrackFormat,
  type Pattern,
  type PatternId,
  type PatternSlot,
  type Slice,
  type Report,
} from "@midi-fantom-utils/midi-util";
import {
  clearSoundfontCache,
  describeSoundfontSource,
  loadSoundfontBytes,
  type SoundfontLoadProgress,
  type SoundfontSource,
} from "@midi-fantom-utils/player-core";
import { FilePicker } from "./components/FilePicker.js";
import { ErrorBanner } from "./components/ErrorBanner.js";
import { SettingsModal } from "./components/SettingsModal.js";
import { HeaderInfo } from "./components/HeaderInfo.js";
import { TimelinePlayer } from "./components/TimelinePlayer.js";
import { TrackLanes } from "./components/TrackLanes.js";
import { TrackList } from "./components/TrackList.js";
import type { PianoRollMarker } from "./components/PianoRoll.js";
import { SliceBar } from "./components/SliceBar.js";
import { PatternDialog, type ChannelNoteCount } from "./components/PatternDialog.js";
import { PatternScreen } from "./components/PatternScreen.js";
import { PatternEditor } from "./components/PatternEditor.js";
import { ExportBar } from "./components/ExportBar.js";
import { useCatalog } from "./useCatalog.js";
import { usePatternStore } from "./patterns/store.js";
import { applyChannelPrograms, channelProgram } from "./channelInstruments.js";
import { useProgramMap } from "./useProgramMap.js";
import {
  loadSettings,
  saveSettings,
  APP_VERSION,
  IS_DEV,
  type MidiPortSelection,
  type Settings,
  type SoundOutput,
} from "./config.js";

type AppState =
  | { kind: "idle" }
  | { kind: "parsing"; file: File }
  | {
      kind: "ready";
      file: File;
      sourceMidi: Midi;
      sourceReport: Report;
    }
  | { kind: "error"; message: string };

type SoundfontLoadState =
  | { status: "idle" }
  | { status: "loading"; progress: SoundfontLoadProgress | null }
  | { status: "ready" }
  | { status: "error" };

const soundfontKey = (s: SoundfontSource): string => `${s.kind}:${s.url}`;

const formatMb = (p: SoundfontLoadProgress): string => {
  const mb = (b: number): string => `${(b / (1024 * 1024)).toFixed(1)} МБ`;
  return p.total ? `${mb(p.loaded)} / ${mb(p.total)}` : mb(p.loaded);
};

const stripExt = (name: string): string => {
  const idx = name.lastIndexOf(".");
  return idx >= 0 ? name.slice(0, idx) : name;
};

// Первая метка всегда в M1, не удаляется. id зафиксирован как "m0".
const FIRST_MARKER: PianoRollMarker = { id: "m0", measure: 1, name: "Начало" };

export const App = () => {
  const [state, setState] = useState<AppState>({ kind: "idle" });
  const [settings, setSettings] = useState<Settings>(() => loadSettings());
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [headerCollapsed, setHeaderCollapsed] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [editingPatternId, setEditingPatternId] = useState<PatternId | null>(null);
  const [tick, setTick] = useState(0);
  // Видимые дорожки (id `${channel}:${program}`) в таймлайне (сессия).
  // Сбрасывается на новом файле на все использованные дорожки.
  const [enabledLanes, setEnabledLanes] = useState<ReadonlySet<string>>(new Set());
  // Заглушенные (mute) дорожки: их ноты не играют и не попадают в срезы/экспорт.
  const [mutedLanes, setMutedLanes] = useState<ReadonlySet<string>>(new Set());
  // Вертикальный скролл лейнов (для синхронизации TrackList).
  const [trackScrollY, setTrackScrollY] = useState(0);
  // Переопределения инструментов каналов: channel → GM program 0..127.
  // Сбрасывается на новом файле (см. handleFile/reset).
  const [instrumentOverrides, setInstrumentOverrides] = useState<ReadonlyMap<number, number>>(
    new Map()
  );
  // Предупреждение, что подготовленные срезы могут быть неактуальными после
  // смены инструмента канала. Снимается повторным «Подготовить срезы».
  const [slicesStale, setSlicesStale] = useState(false);
  // Метки над таймлайном (по-файлу).
  const [markers, setMarkers] = useState<PianoRollMarker[]>([]);
  const nextMarkerIdRef = useRef(1);
  // Срез, из которого открыт PatternDialog (клик по срезу в SliceBar).
  const [bindingSlice, setBindingSlice] = useState<Slice | null>(null);
  // Предзагрузка SoundFont: статус скачивания байтов SF2 текущего источника.
  const [sfLoad, setSfLoad] = useState<SoundfontLoadState>({ status: "idle" });
  // Ошибка загрузки звука (не блокирует UI; старый шрифт остаётся активным).
  const [soundfontError, setSoundfontError] = useState<string | null>(null);
  // Ключ и сам источник успешно загруженного шрифта (для проверок и отката).
  const loadedSfKeyRef = useRef<string | null>(null);
  const loadedSfSourceRef = useRef<SoundfontSource | null>(null);
  const appRef = useRef<HTMLDivElement | null>(null);

  const skipFirstPersist = useRef(true);
  useEffect(() => {
    if (skipFirstPersist.current) {
      skipFirstPersist.current = false;
      return;
    }
    saveSettings(settings);
  }, [settings]);

  // Предзагрузка звука: байты SF2 качаются сразу после выбора источника (и при
  // старте с сохранённым шрифтом). UI заблокирован оверлеем, виден прогресс.
  // Старый шрифт остаётся в памяти (движок держит свои инстансы), пока новый
  // полностью не скачается — по успеху кэш старого освобождается. При ошибке —
  // откатываемся на старый источник (если был) и показываем баннер.
useEffect(() => {
    if (settings.soundOutput !== "sf2") {
      // MIDI-режим: SF2 не нужен — не качаем байты и не показываем оверлей.

      setSfLoad({ status: "idle" });
      return;
    }
    const key = soundfontKey(settings.soundfont);
    if (loadedSfKeyRef.current === key) {
      setSfLoad({ status: "ready" });
      return;
    }
    let cancelled = false;
    setSfLoad({ status: "loading", progress: null });
    const url = settings.soundfont.url;
    loadSoundfontBytes(settings.soundfont, (progress) => {
      if (!cancelled) {
        setSfLoad((s) => (s.status === "loading" ? { ...s, progress } : s));
      }
    })
      .then(() => {
        if (cancelled) return;
        loadedSfKeyRef.current = key;
        loadedSfSourceRef.current = settings.soundfont;
        // Новый шрифт полностью загружен— старые байты SF2 из кэша можно
        // освободить («старый выгружается из памяти»).
        clearSoundfontCache(url);
        setSfLoad({ status: "ready" });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : String(err);
        setSoundfontError(msg);
        const prev = loadedSfSourceRef.current;
        if (prev) {
          setSettings((s) => ({ ...s, soundfont: prev }));
        }
        setSfLoad({ status: "error" });
      });
    return () => {
      cancelled = true;
    };
  }, [settings.soundfont, settings.soundOutput]);

  // Пока звук качается — контент приложения не должен принимать фокус/клики
  // (оверлей перекрывает экран; `inert` дополнительно отключает клавиатуру).
  useEffect(() => {
    const el = appRef.current;
    if (!el) return;
    if (sfLoad.status === "loading") el.setAttribute("inert", "");
    else el.removeAttribute("inert");
  }, [sfLoad.status]);

  const catalog = useCatalog();
  const patternStore = usePatternStore();

  const resetLanes = useCallback((midi: Midi) => {
    const used = new Set<string>();
    for (const lane of buildTrackLanes(midi)) {
      used.add(lane.id);
    }
    setEnabledLanes(used);
    setMutedLanes(new Set());
    setTrackScrollY(0);
  }, []);

  const resetMarkers = useCallback(() => {
    setMarkers([FIRST_MARKER]);
    nextMarkerIdRef.current = 1;
  }, []);

  const handleFile = useCallback(
    async (file: File) => {
      setState({ kind: "parsing", file });
      try {
        const buffer = await file.arrayBuffer();
        const sourceMidi = parseMidi(new Uint8Array(buffer));
        const sourceReport = buildReport(sourceMidi, file.name);
        setState({
          kind: "ready",
          file,
          sourceMidi,
          sourceReport,
        });
        patternStore.reset();
        setInstrumentOverrides(new Map());
        setSlicesStale(false);
        resetLanes(sourceMidi);
        resetMarkers();
      } catch (err) {
        setState({
          kind: "error",
          message: `Не удалось разобрать файл "${file.name}": ${
            (err as Error).message ?? err
          }`,
        });
      }
    },
    [patternStore, resetLanes, resetMarkers]
  );

  const reset = useCallback(() => {
    setState({ kind: "idle" });
    setDownloadError(null);
    patternStore.reset();
    setInstrumentOverrides(new Map());
    setSlicesStale(false);
    setMarkers([FIRST_MARKER]);
    nextMarkerIdRef.current = 1;
    setEnabledLanes(new Set());
    setMutedLanes(new Set());
    setTrackScrollY(0);
  }, [patternStore]);

  const sourceMidi = state.kind === "ready" ? state.sourceMidi : null;

  // Каналы и число нот (для чипов-переключателей и PatternDialog).
  const channelCounts = useMemo<ChannelNoteCount[]>(() => {
    if (!sourceMidi) return [];
    const counts = new Map<number, number>();
    for (const t of sourceMidi.tracks) {
      for (const n of t.notes) {
        const ch = n.channel ?? t.channel;
        counts.set(ch, (counts.get(ch) ?? 0) + 1);
      }
    }
    return [...counts.entries()]
      .map(([channel, noteCount]) => ({ channel, noteCount }))
      .sort((a, b) => a.channel - b.channel);
  }, [sourceMidi]);

  const boundaries = useMemo<number[]>(() => {
    if (!sourceMidi) return [];
    let maxEnd = 0;
    for (const t of sourceMidi.tracks) {
      for (const n of t.notes) maxEnd = Math.max(maxEnd, n.ticks + n.durationTicks);
    }
    const endTick = Math.max(maxEnd, sourceMidi.header.ppq * 4);
    return measureBoundaries(
      sourceMidi.header.ppq,
      sourceMidi.header.timeSignatures,
      endTick
    );
  }, [sourceMidi]);

  // MIDI для piano roll — всегда per-channel раскладка (marker-воркфлоу).
  const pianoRollMidi = useMemo<Midi | null>(() => {
    if (!sourceMidi) return null;
    return toMultiTrackFormat(sourceMidi);
  }, [sourceMidi]);

  // Единая точка правды для downstream: piano roll, «Прослушать», PatternDialog.
  // `applyChannelPrograms` — no-op (ref-равенство) при пустых overrides.
  const effectiveMidi = useMemo<Midi | null>(() => {
    if (!pianoRollMidi) return null;
    return applyChannelPrograms(pianoRollMidi, instrumentOverrides);
  }, [pianoRollMidi, instrumentOverrides]);

  const programMap = useProgramMap(
    settings.soundfont,
    settings.soundOutput === "sf2"
  );

  // Дорожки (лейны) для отображения: канал + программа. Строятся из полного
  // `effectiveMidi` (mute не скрывает лейн — он его затемняет).
  const trackLanes = useMemo(() => {
    if (!effectiveMidi) return [];
    return buildTrackLanes(effectiveMidi, (channel, program) => {
      if (channel === DRUM_CHANNEL) return "";
      return programMap.melodic.find((m) => m.program === program)?.name ?? "";
    });
  }, [effectiveMidi, programMap.melodic]);

  // MIDI без нот muted-дорожек — источник для воспроизведения, срезов,
  // паттернов и экспорта.
  const mutedMidi = useMemo<Midi | null>(() => {
    if (!effectiveMidi) return null;
    return dropMutedLaneNotes(effectiveMidi, mutedLanes);
  }, [effectiveMidi, mutedLanes]);

  const sourcePlayback = useMemo<ReturnType<typeof buildPlaybackData> | null>(() => {
    if (!mutedMidi) return null;
    return buildPlaybackData(mutedMidi);
  }, [mutedMidi]);

  // GM-программа на канал из effectiveMidi (для селекторов инструментов).
  const lanePrograms = useMemo<ReadonlyMap<number, number>>(() => {
    const map = new Map<number, number>();
    if (effectiveMidi) {
      for (const channel of Array.from({ length: 16 }, (_, i) => i)) {
        map.set(channel, channelProgram(effectiveMidi, channel));
      }
    }
    return map;
  }, [effectiveMidi]);

  // Смена инструмента канала: копируем override и помечаем срезы «устаревшими»,
  // если они уже подготовлены.
  const setInstrument = useCallback(
    (channel: number, program: number) => {
      setInstrumentOverrides((prev) => {
        const next = new Map(prev);
        next.set(channel, program);
        return next;
      });
      if (patternStore.state.slices.length > 0) setSlicesStale(true);
    },
    [patternStore.state.slices.length]
  );

  const fileBaseName = state.kind === "ready" ? stripExt(toFantomName(state.file.name)) : "";

  const markerMeasureAtTick = useCallback(
    (tick: number): number => {
      let idx = 0;
      for (let i = boundaries.length - 1; i >= 0; i -= 1) {
        if (boundaries[i]! <= tick) {
          idx = i;
          break;
        }
      }
      // Мера всегда в [1, boundaries.length - 1]: клик на/после конца файла
      // не должен давать меру `boundaries.length` (нулевой срез).
      return Math.min(idx + 1, Math.max(1, boundaries.length - 1));
    },
    [boundaries]
  );

  const addMarker = useCallback(
    (tick: number) => {
      const measure = markerMeasureAtTick(tick);
      if (markers.some((m) => m.measure === measure)) return;
      const marker: PianoRollMarker = {
        id: `m${nextMarkerIdRef.current++}`,
        measure,
        name: "",
      };
      setMarkers((prev) =>
        [...prev, marker].sort((a, b) => a.measure - b.measure)
      );
    },
    [markerMeasureAtTick, markers]
  );

  const moveMarker = useCallback(
    (id: string, tick: number) => {
      const measure = markerMeasureAtTick(tick);
      setMarkers((prev) => {
        // Не ставим на меру, уже занятую другой меткой (иначе дубль).
        if (prev.some((m) => m.id !== id && m.measure === measure)) return prev;
        return prev
          .map((m) => (m.id === id ? { ...m, measure } : m))
          .sort((a, b) => a.measure - b.measure);
      });
    },
    [markerMeasureAtTick]
  );

  const renameMarker = useCallback((id: string, name: string) => {
    setMarkers((prev) => prev.map((m) => (m.id === id ? { ...m, name } : m)));
  }, []);

  const deleteMarker = useCallback((id: string) => {
    // Первую метку нельзя удалить (всегда остаётся хотя бы одна), но её
    // можно переместить. Остальные метки удаляются свободно.
    setMarkers((prev) =>
      prev.filter((m) => m.id !== id || m.id === FIRST_MARKER.id)
    );
  }, []);

  const toggleLane = useCallback((laneId: string) => {
    setEnabledLanes((prev) => {
      const next = new Set(prev);
      if (next.has(laneId)) next.delete(laneId);
      else next.add(laneId);
      return next;
    });
  }, []);

  const toggleMute = useCallback((laneId: string) => {
    setMutedLanes((prev) => {
      const next = new Set(prev);
      if (next.has(laneId)) next.delete(laneId);
      else next.add(laneId);
      return next;
    });
    // Mute влияет на срезы/экспорт — если срезы уже готовы, помечаем stale.
    if (patternStore.state.slices.length > 0) setSlicesStale(true);
  }, [patternStore.state.slices.length]);

  // «Подготовить срезы»: сегмент × включённый канал. Повторный клик
  // пересоздаёт (сначала очищаем существующие срезы). Срезы остаются
  // per-channel (совместимость с Fantom-экспортом); видимость дорожки
  // управляет тем, попадает ли её канал в срезы.
  const prepareSlices = useCallback(() => {
    if (markers.length === 0 || !sourceMidi) return;
    const sorted = [...markers].sort((a, b) => a.measure - b.measure);
    const endMeasure = boundaries.length;
    // Каналы видимых дорожек (не muted — их ноты исключены из срезов/экспорта).
    const channels = new Set<number>();
    for (const lane of trackLanes) {
      if (!enabledLanes.has(lane.id)) continue;
      if (mutedLanes.has(lane.id)) continue;
      channels.add(lane.channel);
    }
    for (const s of patternStore.state.slices) patternStore.removeSlice(s.id);
    for (let i = 0; i < sorted.length; i += 1) {
      const startMeasure = sorted[i]!.measure;
      const nextMeasure = sorted[i + 1]?.measure ?? endMeasure;
      if (nextMeasure <= startMeasure) continue; // нулевой сегмент не создаём
      for (const ch of channels) {
        patternStore.addSlice({ startMeasure, endMeasure: nextMeasure, channel: ch });
      }
    }
    setSlicesStale(false);
  }, [
    boundaries.length,
    enabledLanes,
    markers,
    mutedLanes,
    patternStore,
    sourceMidi,
    trackLanes,
  ]);

  const handleCreatePattern = useCallback(
    (pattern: Pattern) => {
      patternStore.upsertPattern(pattern);
      if (bindingSlice) {
        patternStore.setSlicePattern(bindingSlice.id, pattern.id);
      }
    },
    [patternStore, bindingSlice]
  );

  // Экспресс-создание паттерна из среза: track = channel + 1, слот = выбранная
  // вариация. Паттерн сразу появляется в гриде. Имя/длина — дефолтные
  // (buildPattern). При занятом слоте — перезапись (как в PatternDialog).
  const handleSelectVariation = useCallback(
    (slice: Slice, slot: PatternSlot) => {
      if (!mutedMidi) return;
      const track = (slice.range.channel + 1) as Parameters<typeof buildPattern>[1];
      const existing = new Map(patternStore.state.patterns);
      // Конфликтующий паттерн на том же слоте удаляем (перезапись).
      existing.delete(formatPatternId(track, slot));
      const result = buildPattern(
        mutedMidi,
        track,
        slot,
        slice.range,
        {
          fileBaseName,
          existing,
          channel: slice.range.channel as MidiChannel,
        }
      );
      if (!("ok" in result) || result.ok === false) return;
      patternStore.upsertPattern(result.pattern);
      patternStore.setSlicePattern(slice.id, result.pattern.id);
    },
    [fileBaseName, mutedMidi, patternStore]
  );

  const editingPattern = useMemo<Pattern | null>(() => {
    if (!editingPatternId) return null;
    return patternStore.state.patterns.get(editingPatternId) ?? null;
  }, [editingPatternId, patternStore.state.patterns]);

  // Префилл имени для PatternDialog из метки-начала сегмента.
  const bindingDefaultName = useMemo<string | undefined>(() => {
    if (!bindingSlice) return undefined;
    const marker = markers.find((m) => m.measure === bindingSlice.range.startMeasure);
    if (!marker) return undefined;
    const chLabel = bindingSlice.range.channel === 9 ? "9 Drums" : `ch ${bindingSlice.range.channel}`;
    return marker.name ? `${marker.name} · ${chLabel}` : chLabel;
  }, [bindingSlice, markers]);

  const pianoRollHeight = settings.appearance.pianoRoll.height;
  // Высота одного лейна дорожки (px) в лейновом таймлайне.
  const pianoRollLaneHeight = 64;

  return (
    <div className="app" ref={appRef}>
      <header className="app__header">
        <h1>Roland Fantom 06/07/08 MIDI tools</h1>
        <p>Подготовка MIDI-файлов для Roland Fantom 06/07/08 (Pattern workflow)</p>
        <span
          className={`app__version${IS_DEV ? " app__version--dev" : ""}`}
          title="Версия сборки"
        >
          v{APP_VERSION}
        </span>
        <button
          type="button"
          className="gear-icon"
          onClick={() => setSettingsOpen(true)}
          aria-label="Открыть настройки"
          title="Настройки"
        >
          ⚙
        </button>
      </header>

      <main className="app__main">
        {state.kind === "idle" || state.kind === "parsing" ? (
          <FilePicker
            onFile={handleFile}
            disabled={state.kind === "parsing"}
            currentName={state.kind === "parsing" ? state.file.name : undefined}
          />
        ) : null}

        {state.kind === "error" ? (
          <ErrorBanner message={state.message} onDismiss={reset} />
        ) : null}

        {downloadError ? (
          <div className="banner banner--error" role="alert">
            <span>{downloadError}</span>
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => setDownloadError(null)}
            >
              Закрыть
            </button>
          </div>
        ) : null}

        {state.kind === "ready" && sourceMidi && pianoRollMidi ? (
          <>
            <section className="card">
              <div className="app__results-head">
                <div>
                  <strong>{state.file.name}</strong>
                </div>
                <div className="app__results-actions">
                  <button
                    type="button"
                    className="btn btn--ghost"
                    onClick={reset}
                  >
                    Другой файл
                  </button>
                  <button
                    type="button"
                    className="btn btn--ghost"
                    onClick={() => setHeaderCollapsed((v) => !v)}
                  >
                    {headerCollapsed ? "▾ Показать заголовок" : "▸ Скрыть заголовок"}
                  </button>
                </div>
              </div>
              {headerCollapsed ? null : (
                <HeaderInfo
                  title="Исходный файл"
                  kind="source"
                  fileName={state.sourceReport.fileName}
                  format={state.sourceMidi.format}
                  trackCount={state.sourceMidi.tracks.length}
                  durationLabel={state.sourceReport.duration}
                  durationTicks={state.sourceReport.durationTicks}
                  tempos={state.sourceReport.tempos}
                  timeSignatures={state.sourceReport.timeSignatures}
                  keySignatures={state.sourceReport.keySignatures}
                  detectedKey={state.sourceReport.detectedKey}
                />
              )}
            </section>

            <section className="card">
              <TimelinePlayer
                midi={state.sourceMidi}
                playback={sourcePlayback!}
                fileName={state.file.name}
                source={settings.soundfont}
                output={settings.soundOutput}
                midiPort={settings.midiPort}
                onOpenSettings={() => setSettingsOpen(true)}
                onTickChange={setTick}
              />
            </section>

            <section className="card">
              <h2 className="card__title">Таймлайн (лейны дорожек)</h2>
              {slicesStale ? (
                <div className="banner banner--error" role="alert">
                  <span>
                    Инструменты или mute изменены — подготовленные срезы могут
                    быть неактуальными. Нажмите «Подготовить срезы» повторно.
                  </span>
                </div>
              ) : null}
              <div style={{ display: "flex", gap: 0 }}>
                <TrackList
                  lanes={trackLanes}
                  mutedLanes={mutedLanes}
                  enabledLanes={enabledLanes}
                  programs={lanePrograms}
                  programMap={programMap}
                  scrollY={trackScrollY}
                  laneHeight={pianoRollLaneHeight}
                  height={pianoRollHeight}
                  onToggleMute={toggleMute}
                  onToggleVisible={toggleLane}
                  onInstrumentChange={setInstrument}
                />
                <TrackLanes
                  lanes={trackLanes}
                  width={1200}
                  height={pianoRollHeight}
                  laneHeight={pianoRollLaneHeight}
                  ppq={effectiveMidi!.header.ppq}
                  timeSignatures={effectiveMidi!.header.timeSignatures}
                  playheadTick={tick}
                  onSeek={setTick}
                  enabledLanes={enabledLanes}
                  mutedLanes={mutedLanes}
                  markers={markers}
                  onAddMarker={addMarker}
                  onMoveMarker={moveMarker}
                  onRenameMarker={renameMarker}
                  onDeleteMarker={deleteMarker}
                  scrollY={trackScrollY}
                  onScrollYChange={setTrackScrollY}
                />
              </div>
              <div style={{ marginTop: 8, display: "flex", justifyContent: "flex-end" }}>
                <button
                  type="button"
                  className="btn btn--primary"
                  onClick={prepareSlices}
                >
                  Подготовить срезы
                </button>
              </div>
            </section>

            <section className="card">
              <h2 className="card__title">Срезы</h2>
              <SliceBar
                slices={patternStore.state.slices}
                patterns={patternStore.state.patterns}
                onBind={setBindingSlice}
                onSelectVariation={handleSelectVariation}
                onEdit={(sliceId) => {
                  const slice = patternStore.state.slices.find((s) => s.id === sliceId);
                  if (slice?.patternId) setEditingPatternId(slice.patternId);
                }}
                onDelete={(id) => patternStore.removeSlice(id)}
              />
            </section>

            <section className="card">
              <h2 className="card__title">Паттерны</h2>
              <PatternScreen
                patterns={patternStore.state.patterns}
                source={settings.soundfont}
                output={settings.soundOutput}
                midiPort={settings.midiPort}
                onEdit={(id) => setEditingPatternId(id)}
                onDelete={(id) => patternStore.deletePattern(id)}
              />
              <div style={{ marginTop: 12 }}>
                <ExportBar
                  patterns={[...patternStore.state.patterns.values()]}
                  projectName={fileBaseName}
                  onError={setDownloadError}
                />
              </div>
            </section>
          </>
        ) : null}
      </main>

<SettingsModal
        open={settingsOpen}
        source={settings.soundfont}
        onSourceChange={(next: SoundfontSource) => {
          // Смена шрифта — сразу предзагрузка: модалка закрывается, поверх —
          // оверлей с прогрессом,, пока звук качается.,
          setSettingsOpen(false);
          setSettings((s) => ({ ...s, soundfont: next }));
        }}
        soundOutput={settings.soundOutput}
        onSoundOutputChange={(next) =>
          setSettings((s) => ({ ...s, soundOutput: next }))
        }
        midiPort={settings.midiPort}
        onMidiPortChange={(port) =>
          setSettings((s) => ({ ...s, midiPort: port }))
        }
        catalog={catalog}
        timelineHeight={settings.appearance.pianoRoll.height}
        onTimelineHeightChange={(height) =>
          setSettings((s) => ({
            ...s,
            appearance: { pianoRoll: { height } },
          }))
        }
        onClose={() => setSettingsOpen(false)}
      />

      {sfLoad.status === "loading" ? (
        <div className="sf-loading-overlay" role="status" aria-live="polite">
          <div className="sf-loading-overlay__card">
            <div className="sf-loading-overlay__spinner" />
            <p className="sf-loading-overlay__title">Загрузка {describeSoundfontSource(settings.soundfont)}…</p>
            <div className="sf-loading-overlay__bar">
              <div
                className="sf-loading-overlay__fill"
                style={{
                  width:
                    sfLoad.progress && sfLoad.progress.total
                      ? `${(sfLoad.progress.loaded / sfLoad.progress.total) * 100}%`
                      : "100%",
                }}
              />
            </div>
            <p className="sf-loading-overlay__meta">
              {sfLoad.progress ? formatMb(sfLoad.progress) : "…"}
            </p>
          </div>
        </div>
      ) : null}

      {soundfontError ? (
        <div className="banner banner--error" role="alert">
          <span>Не удалось загрузить звук: {soundfontError}</span>
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => setSoundfontError(null)}
          >
            Закрыть
          </button>
        </div>
      ) : null}

      {state.kind === "ready" && sourceMidi && bindingSlice ? (
        <PatternDialog
          open
          source={mutedMidi!}
          range={bindingSlice.range}
          fileBaseName={fileBaseName}
          existing={patternStore.state.patterns}
          channelCounts={channelCounts}
          defaultName={bindingDefaultName}
          onClose={() => setBindingSlice(null)}
          onCreate={(pat) => {
            handleCreatePattern(pat);
            setBindingSlice(null);
          }}
        />
      ) : null}

      {editingPattern ? (
        <PatternEditor
          open
          pattern={editingPattern}
          onSave={(midi) => {
            patternStore.upsertPattern({ ...editingPattern, midi, noteCount: countNotes(midi) });
          }}
          onClose={() => setEditingPatternId(null)}
        />
      ) : null}
    </div>
  );
};
